import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiGet, apiPost, fmtCurrency } from "@/utils/api";

// Sec 194Q WHT codes mirror goods_wht_threshold.py / sap_supplier_write_client.py:
// "54" = Special 1 (0.1%) once the buyer entity's FY-cumulative purchases
// from this vendor cross Rs 50L; "0" = Zero (0%) below that threshold.
const WHT_CODE_LABEL = { "54": "0.1% (Special 1 — code 54)", "0": "0% (Zero)" };

export const SupplierWhtSetupStep = ({ vendorId, siteCode, currency, onContinue }) => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [applyError, setApplyError] = useState("");
  const [applying, setApplying] = useState(false);
  const [overrideCode, setOverrideCode] = useState("auto");

  const isInr = (currency || "INR").toUpperCase() === "INR";

  useEffect(() => {
    apiGet(`/tax-settings/supplier/${encodeURIComponent(vendorId)}?${new URLSearchParams({ site_code: siteCode || "", currency: currency || "INR" })}`)
      .then(setStatus)
      .catch((err) => setLoadError(err.message));
  }, [vendorId, siteCode, currency]);

  const proceed = () => {
    setApplying(true); setApplyError("");
    const params = new URLSearchParams({ site_code: siteCode || "", currency: currency || "INR" });
    if (overrideCode !== "auto") params.set("override_code", overrideCode);
    apiPost(`/tax-settings/supplier/${encodeURIComponent(vendorId)}/apply?${params}`, {})
      .then(() => onContinue())
      .catch((err) => { setApplyError(err.message); setApplying(false); });
  };

  if (loadError) return <div role="alert" className="border-l-2 border-red-600 bg-red-50 p-4 text-sm text-red-900" data-testid="wht-setup-error">{loadError}</div>;
  if (!status) return <div className="flex min-h-40 items-center justify-center border border-slate-200 bg-white" data-testid="wht-setup-loading"><Loader2 size={18} className="animate-spin text-[#076D74]" /><span className="ml-2 text-sm text-slate-600">Checking SAP withholding tax classification…</span></div>;

  const estimate = status.wht_estimate || {};

  if (!isInr) {
    return <section className="border border-slate-200 bg-white p-5" data-testid="wht-setup-step">
      <p className="font-mono text-xs font-semibold uppercase text-[#076D74]">Step 1 of 2 · Supplier setting</p>
      <h2 className="mt-1 text-xl font-heading font-semibold text-slate-950">Sec 194Q withholding tax — {status.vendor_name || vendorId}</h2>
      <div className="mt-4 flex items-start gap-2 border-l-2 border-slate-400 bg-slate-50 p-3 text-sm text-slate-700" data-testid="wht-setup-not-applicable">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <span>Sec 194Q withholding tax applies to INR vendors only — not applicable for this <strong>{currency}</strong> purchase order.</span>
      </div>
      <div className="mt-5"><Button onClick={onContinue} className="gap-2 bg-[#0A6755] hover:bg-[#076D74]" data-testid="wht-setup-continue-button">Continue to materials</Button></div>
    </section>;
  }

  return <section className="border border-slate-200 bg-white p-5" data-testid="wht-setup-step">
    <p className="font-mono text-xs font-semibold uppercase text-[#076D74]">Step 1 of 2 · Supplier setting</p>
    <h2 className="mt-1 text-xl font-heading font-semibold text-slate-950">Sec 194Q withholding tax — {status.vendor_name || vendorId}</h2>
    <p className="mt-1 text-xs text-slate-500">Buyer entity site <span className="font-mono font-semibold text-slate-800">{siteCode || "—"}</span>{status.company_code && <span> ({status.company_code})</span>}</p>

    {estimate.error
      ? <div className="mt-4 flex items-start gap-2 border-l-2 border-red-600 bg-red-50 p-3 text-sm text-red-900" data-testid="wht-setup-threshold-error"><ShieldAlert size={16} className="mt-0.5 shrink-0" /><span>{estimate.reason}</span></div>
      : <div className="mt-4 border border-slate-200 bg-slate-50 p-4" data-testid="wht-setup-cumulative-business">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current FY purchases from this supplier at this entity</p>
          <p className="mt-1 text-2xl font-heading font-semibold text-slate-950" data-testid="wht-setup-fy-cumulative-value">
            {estimate.fy_cumulative != null ? fmtCurrency(estimate.fy_cumulative, "INR") : "—"}
          </p>
          <p className="mt-2 text-sm text-slate-700">
            Withholding tax to apply: <strong data-testid="wht-setup-resolved-code">{WHT_CODE_LABEL[estimate.code] || "Not determined"}</strong>
          </p>
          <p className="mt-1 text-xs text-slate-500" data-testid="wht-setup-reason">{estimate.reason}</p>
        </div>}

    <div className="mt-4 flex items-start gap-2 border-l-2 border-slate-300 bg-white p-3 text-xs text-slate-600" data-testid="wht-setup-current-master">
      Current SAP vendor-master setting: <strong className="ml-1">{status.rate_type_label}</strong>
    </div>

    <details className="mt-4 group" data-testid="wht-setup-override-details">
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">Finance testing override</summary>
      <div className="mt-2 max-w-xs">
        <Select value={overrideCode} onValueChange={setOverrideCode}>
          <SelectTrigger data-testid="wht-setup-override-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto" data-testid="wht-setup-override-auto">Auto (FY-cumulative, recommended)</SelectItem>
            <SelectItem value="0" data-testid="wht-setup-override-zero">Force 0% (testing only)</SelectItem>
            <SelectItem value="54" data-testid="wht-setup-override-special">Force 0.1% (testing only)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </details>

    {applyError && <div role="alert" className="mt-4 border-l-2 border-red-600 bg-red-50 p-3 text-sm text-red-900" data-testid="wht-setup-apply-error">{applyError}</div>}

    <div className="mt-5 flex flex-wrap gap-3">
      <Button onClick={proceed} disabled={applying || estimate.error} className="gap-2 bg-[#0A6755] hover:bg-[#076D74]" data-testid="wht-setup-proceed-button">
        {applying ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}Proceed
      </Button>
    </div>
  </section>;
};
